import { toast } from "react-toastify";
import { TokenManager, SESSION_EXPIRY_KEY } from "./tokenManager";
import appPreloader from "../services/appPreloader";

class InactivityTimer {
  constructor() {
    this.timeout = 8 * 60 * 60 * 1000;
    this.warningTimeout = 8 * 60 * 60 * 1000 - 60 * 1000;
    this.timeoutId = null;
    this.warningId = null;
    this.isWarningShown = false;
    this.lastActivityTs = 0;
    // Activity events (especially mousemove/scroll) fire hundreds of times per
    // second. Refreshing the session on every event means a synchronous
    // localStorage write on each one, which blocks the main thread and makes the
    // UI stutter (very noticeable while dragging/resizing the window). A coarse
    // throttle is plenty for an 8-hour timeout.
    this.activityThrottleMs = 1000;

    this.events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"];

    // Stable references are required for removeEventListener().
    this.handleActivity = this.handleActivity.bind(this);
    this.handleStorage = this.handleStorage.bind(this);
  }

  start() {
    if (!TokenManager.isLoggedIn()) {
      this.stop();
      return;
    }

    this.clearTimeouts();
    this.removeEventListeners();
    this.addEventListeners();
    window.removeEventListener("storage", this.handleStorage);
    window.addEventListener("storage", this.handleStorage);
    this.scheduleFromStoredExpiry();
  }

  stop() {
    this.clearTimeouts();
    this.removeEventListeners();
    window.removeEventListener("storage", this.handleStorage);
    this.isWarningShown = false;
  }

  scheduleFromStoredExpiry() {
    const expiresAt = TokenManager.getSessionExpiry();

    if (!expiresAt || Date.now() >= expiresAt) {
      this.logout(false);
      return;
    }

    this.clearTimeouts();

    const remaining = expiresAt - Date.now();
    const warningDelay = Math.max(0, remaining - 60 * 1000);

    this.warningId = setTimeout(() => this.showWarning(expiresAt), warningDelay);

    this.timeoutId = setTimeout(() => {
      const latestExpiry = TokenManager.getSessionExpiry();

      if (latestExpiry && Date.now() < latestExpiry) {
        this.scheduleFromStoredExpiry();
      } else {
        this.logout(true);
      }
    }, remaining);
  }

  clearTimeouts() {
    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.warningId) clearTimeout(this.warningId);
    this.timeoutId = null;
    this.warningId = null;
  }

  showWarning(expectedExpiry) {
    const currentExpiry = TokenManager.getSessionExpiry();

    if (this.isWarningShown || !currentExpiry || currentExpiry !== expectedExpiry || !TokenManager.isLoggedIn()) {
      if (currentExpiry && currentExpiry !== expectedExpiry) this.scheduleFromStoredExpiry();
      return;
    }

    this.isWarningShown = true;

    toast.warning(
      "You will be logged out in 1 minute due to inactivity. Move your mouse or press any key to stay logged in.",
      {
        position: "top-right",
        autoClose: 55000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        onClose: () => {
          this.isWarningShown = false;
          if (TokenManager.isLoggedIn()) this.scheduleFromStoredExpiry();
        },
      }
    );
  }

  handleActivity(event) {
    if (event?.target?.closest?.(".Toastify")) return;

    const now = Date.now();
    if (now - this.lastActivityTs < this.activityThrottleMs) return;
    this.lastActivityTs = now;

    if (!TokenManager.isLoggedIn()) {
      this.logout(false);
      return;
    }

    TokenManager.touchSession();
    this.isWarningShown = false;
    this.scheduleFromStoredExpiry();
  }

  handleStorage(event) {
    if (event.key === SESSION_EXPIRY_KEY) {
      if (!TokenManager.isLoggedIn()) {
        this.logout(false);
        return;
      }
      this.isWarningShown = false;
      this.scheduleFromStoredExpiry();
    }

    if (event.key === "token" && event.newValue === null) {
      this.logout(false);
    }
  }

  addEventListeners() {
    this.events.forEach((event) => document.addEventListener(event, this.handleActivity, false));
  }

  removeEventListeners() {
    this.events.forEach((event) => document.removeEventListener(event, this.handleActivity, false));
  }

  logout(showMessage = true) {
    TokenManager.clearSession();
    appPreloader.clearCache();
    this.stop();

    if (showMessage) {
      toast.error("You have been logged out due to inactivity.", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }

    // replace() removes the expired dashboard entry from normal back navigation.
    if (window.location.pathname !== "/login") {
      window.location.replace("/login");
    }
  }
}

const inactivityTimer = new InactivityTimer();
export default inactivityTimer;
